import { useState } from "react";
import { Search } from "lucide-react";
import { FEATURED_DESTINATIONS, ALL_COUNTRIES } from "@/data/turboData";

const Marquee = ({ items }) => (
  <div className="relative overflow-hidden py-3">
    <div className="flex w-max animate-marquee">
      {[...items, ...items].map((c, i) => (
        <div
          key={i}
          className="mx-2 inline-flex items-center gap-2 rounded-full border border-[var(--tt-line)] bg-white px-4 py-2 text-sm whitespace-nowrap"
        >
          <span className="h-1.5 w-1.5 rounded-full bg-[var(--tt-accent)]" />
          {c}
        </div>
      ))}
    </div>
  </div>
);

const WhereWeShip = () => {
  const [query, setQuery] = useState("");
  const filtered = query
    ? ALL_COUNTRIES.filter((c) => c.toLowerCase().includes(query.toLowerCase())).slice(0, 16)
    : null;

  return (
    <section className="relative py-20 md:py-28">
      <div className="mx-auto max-w-7xl px-4 sm:px-6">
        <div className="grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-5">
            <div className="text-xs uppercase tracking-[0.2em] text-[var(--tt-accent)] font-semibold">
              Where we ship
            </div>
            <h2 className="font-display text-4xl md:text-5xl font-bold text-[var(--tt-navy)] mt-3 leading-[1.05]">
              180+ countries.
              <br />One simple quote.
            </h2>
            <p className="mt-5 text-[var(--tt-ink-soft)] leading-relaxed max-w-md">
              From Detroit to Dakar, Troy to Trinidad. Type a country to see if we
              deliver there — yes, we probably do.
            </p>

            <div className="mt-7 relative">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-[var(--tt-muted)]" />
              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search a country…"
                className="w-full rounded-full border border-[var(--tt-line)] bg-white pl-10 pr-4 py-3.5 text-sm outline-none focus:border-[var(--tt-navy)] transition"
              />
              {filtered && filtered.length > 0 && (
                <div className="absolute z-10 mt-2 w-full rounded-2xl border border-[var(--tt-line)] bg-white shadow-xl overflow-hidden">
                  {filtered.map((c) => (
                    <div
                      key={c}
                      className="px-4 py-2.5 text-sm hover:bg-[var(--tt-bg-soft)] cursor-pointer flex items-center justify-between"
                    >
                      <span>{c}</span>
                      <span className="text-xs text-[var(--tt-accent)] font-semibold">Available</span>
                    </div>
                  ))}
                </div>
              )}
              {filtered && filtered.length === 0 && (
                <div className="absolute z-10 mt-2 w-full rounded-2xl border border-[var(--tt-line)] bg-white shadow-xl px-4 py-3 text-sm text-[var(--tt-muted)]">
                  Hmm, we couldn&apos;t match that. Try a different spelling, or
                  contact us for custom destinations.
                </div>
              )}
            </div>
          </div>

          <div className="lg:col-span-7">
            <div className="rounded-3xl overflow-hidden border border-[var(--tt-line)] bg-gradient-to-br from-[var(--tt-navy)] to-[var(--tt-navy-2)] p-6 md:p-8 relative">
              {/* Dotted world map illustration */}
              <svg viewBox="0 0 600 320" className="w-full h-auto opacity-90">
                <defs>
                  <pattern id="dots" x="0" y="0" width="8" height="8" patternUnits="userSpaceOnUse">
                    <circle cx="2" cy="2" r="1" fill="rgba(255,255,255,0.18)" />
                  </pattern>
                </defs>
                {/* Simplified continents using rounded rects/paths */}
                <g fill="url(#dots)">
                  {/* North America */}
                  <path d="M40,80 Q60,40 130,50 L170,70 L200,100 L180,150 L140,170 L80,160 L50,130 Z" />
                  {/* South America */}
                  <path d="M150,180 L200,180 L220,220 L200,290 L170,300 L155,260 L150,210 Z" />
                  {/* Europe */}
                  <path d="M280,60 L340,55 L370,80 L360,110 L320,115 L290,100 Z" />
                  {/* Africa */}
                  <path d="M290,130 L360,125 L380,160 L370,220 L340,260 L310,250 L295,200 L290,160 Z" />
                  {/* Asia */}
                  <path d="M380,55 L500,50 L540,80 L560,140 L520,160 L470,140 L420,130 L390,110 L380,85 Z" />
                  {/* Australia */}
                  <path d="M490,220 L550,215 L560,250 L530,265 L495,255 Z" />
                </g>
                {/* Connection lines */}
                <g stroke="var(--tt-accent-2)" strokeWidth="1.5" fill="none" strokeDasharray="3 3" opacity="0.7">
                  <path d="M120,110 Q200,30 320,90" />
                  <path d="M120,110 Q230,140 340,180" />
                  <path d="M120,110 Q300,260 460,235" />
                </g>
                {/* Hub */}
                <circle cx="120" cy="110" r="6" fill="#d4e9cf" />
                <circle cx="120" cy="110" r="11" fill="none" stroke="#d4e9cf" strokeWidth="2" opacity="0.6">
                  <animate attributeName="r" from="6" to="20" dur="2s" repeatCount="indefinite" />
                  <animate attributeName="opacity" from="0.7" to="0" dur="2s" repeatCount="indefinite" />
                </circle>
                {/* Destination dots */}
                {[[320, 90], [340, 180], [460, 235], [400, 145]].map(([cx, cy], i) => (
                  <g key={i}>
                    <circle cx={cx} cy={cy} r="4" fill="#eaf3e5" />
                    <circle cx={cx} cy={cy} r="8" fill="none" stroke="#eaf3e5" strokeWidth="1.5" opacity="0.5" />
                  </g>
                ))}
              </svg>

              <div className="absolute top-6 left-6 inline-flex items-center gap-2 rounded-full bg-white/10 backdrop-blur px-3 py-1.5 text-xs text-white">
                <span className="h-1.5 w-1.5 rounded-full bg-[var(--tt-accent)] pulse-dot" />
                Hub: Troy, MI · USA
              </div>
            </div>
          </div>
        </div>

        <div className="mt-12">
          <div className="text-xs uppercase tracking-[0.2em] text-[var(--tt-muted)] font-semibold mb-3">
            Top destinations this month
          </div>
          <Marquee items={FEATURED_DESTINATIONS} />
        </div>
      </div>
    </section>
  );
};

export default WhereWeShip;
